import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Typography } from '@/components/Typography';

import { ReactComponent as VideoPlay } from '@/assets/icons/video/centerPlay.svg';
import { ReactComponent as VideoPause } from '@/assets/icons/video/pause.svg';

export const VideoModal = styled(Box)`
  background-color: ${({ theme }) => theme.colors?.black};
  border-top: 3px solid ${({ theme }) => theme.colors?.goldenRod};
  bottom: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  position: fixed;
  width: 100%;
  z-index: 999;
`;
export const CloseButton = styled(Box)`
  cursor: pointer;
  top: 5px;
  right: 5px;
  position: absolute;
  z-index: ${({ theme }) => theme.zIndex.modalContent};
  color: white;
  width: 56px;
  height: 56px;
  text-align: center;
  line-height: 56px;
  background: rgba(211, 211, 211, 0.15);
  backdrop-filter: blur(13.125px);
`;
export const PlayerWrapper = styled(Box)`
  align-self: center;
  height: 100%;
  max-height: 100%;
  max-width: 100%;
  position: relative;
  width: 100%;
  &:hover .controls.hover {
    opacity: 1;
    transform: translateY(0);
    pointer-events: all;
  }
  &:hover .player-overlay.hover {
    opacity: 1;
  }
`;

export const PlayerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  pointer-events: none;
  opacity: 0;
  &.show {
    opacity: 1;
  }
`;

export const VideoBox = styled(Box)<{
  $currentTime?: number;
  $isEnded?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ $currentTime }) =>
    $currentTime !== 0 &&
    css`
      z-index: 1;
    `}
  ${({ $isEnded }) =>
    !!$isEnded &&
    css`
      z-index: unset;
    `}
`;
export const PlayerVideo = styled.video<{
  $aspectRatio?: { x: number; y: number };
  $desktopAspectRatio?: { x: number; y: number };
  $mobileAspectRatio?: { x: number; y: number };
  controls?: boolean;
}>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  margin: 0 auto;
  ${({ $mobileAspectRatio }) =>
    $mobileAspectRatio &&
    css`
      aspect-ratio: ${$mobileAspectRatio.x} / ${$mobileAspectRatio.y};
    `}
  ${({ $desktopAspectRatio, theme }) =>
    $desktopAspectRatio &&
    css`
      ${theme.media.md.up} {
        aspect-ratio: ${$desktopAspectRatio.x} / ${$desktopAspectRatio.y};
      }
    `}

  ${({ controls }) =>
    !controls &&
    css`
      pointer-events: none;
    `}
`;
export const PlayButtonCenter = styled.button`
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  padding: 0;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 3;
  width: 64px;
  height: 64px;
  background: rgb(211, 211, 211, 0.3);
  opacity: 0;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  &.show {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const PlayIconLarge = styled(VideoPlay)`
  path:nth-child(2) {
    fill: ${({ theme }) => theme.colors?.white};
    transition: fill 0.3s;
  }
  path:nth-child(1) {
    stroke: ${({ theme }) => theme.colors?.white};
    transition: stroke 0.3s;
  }
  transform: translateX(3px);
`;

export const PlayIconSmall = styled(VideoPlay)`
  transform: translateX(2px) scale(0.8);
  path:nth-child(2) {
    fill: ${({ theme }) => theme.colors?.white};
    transition: fill 0.3s;
  }
  path:nth-child(1) {
    stroke: ${({ theme }) => theme.colors?.white};
    transition: stroke 0.3s;
  }
`;

const cornerButtonStyles = css`
  position: absolute;
  border-radius: 50%;
  right: 0;
  top: 3rem;
  padding: 0;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: ${({ theme }) => theme.zIndex.modalContent};
  width: 48px;
  height: 48px;
  background: rgba(211, 211, 211, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;

  ${({ theme }) => theme.media.md.up} {
    top: unset;
    bottom: 0;
  }
`;

export const PlayButtonCorner = styled.button`
  ${cornerButtonStyles}
  pointer-events: auto;
`;

export const PauseButtonCorner = styled.button`
  ${cornerButtonStyles}
`;

export const PauseIconLarge = styled(VideoPause)`
  rect {
    fill: ${({ theme }) => theme.colors?.white};
    transition: fill 0.3s;
  }
  path {
    stroke: ${({ theme }) => theme.colors?.white};
    transition: stroke 0.3s;
  }
`;

export const PlayerControls = styled(Box)`
  bottom: 0;
  left: 0;
  height: 3.5rem;
  padding: 1rem;
  position: absolute;
  width: 100%;
  z-index: 10;
  background: rgba(0, 0, 0, 0);
  opacity: 0;
  padding: 8px 16px;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  &.show {
    opacity: 1;
    transform: translateY(0);
    pointer-events: all;
  }
  &.hide {
    opacity: 0;
    transform: translateY(100%);
    pointer-events: none;
  }
`;
export const StyledPlayAndPauseButton = styled.button`
  cursor: grab;
  background: unset;
  border: none;
  border-radius: 0;
  color: white;
  flex: 1 auto;
  margin: 0;
  outline: none;
  padding: 0;
  text-align: center;
  width: 1rem;
  border-bottom-left-radius: 0.2em;
  border-top-left-radius: 0.2em;
`;
export const StyledMuteButton = styled.button`
  background: unset;
  cursor: grab;
  border: none;
  border-radius: 0;
  color: white;
  flex: 1 auto;
  margin: 0;
  outline: none;
  padding: 0;
  text-align: center;
  width: 1rem;
`;

export const StyledToggleScreenButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: unset;

  cursor: grab;
  border: none;
  border-radius: 0;
  color: white;
  flex: 1 auto;
  margin: 0;
  outline: none;
  padding: 0;
  text-align: center;
  width: 1rem;
  border-bottom-right-radius: 0.2em;
  border-top-right-radius: 0.2em;
`;

export const PlayersControlsProgressBar = styled(Box)`
  width: 100%;
  height: 4px;
  flex: 1 100%;
  position: relative;
  margin-bottom: 8px;
`;
export const StyledProgress = styled.progress`
  cursor: grab;
  appearance: none;
  background-color: #666;
  border: none;
  box-shadow: inset -0.2em 0.2em 0.4em rgba(0, 0, 0, 0.2);
  color: ${({ theme }) => theme.colors?.goldenRod};
  height: 100%;
  position: absolute;
  width: 100%;
  ::-webkit-progress-value {
    background: ${({ theme }) => theme.colors?.goldenRod};
  }
`;

export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`;

export const ProgressText = styled(Typography)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    color: ${theme.colors.white};
  `}
`;

export const SettingsMenu = styled.div`
  position: absolute;
  right: 16px;
  bottom: 40px;
  padding: 10px;
  background: white;
`;

export const SettingsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const SettingsItem = styled.li``;
